import React from 'react';
import { string, oneOfType, bool } from 'prop-types';
import { injectIntl, intlShape } from '../../util/reactIntl';
import classNames from 'classnames';
import { propTypes } from '../../util/types';
import {
  ensureUser,
  ensureCurrentUser,
  userDisplayNameAsString,
  userAbbreviatedName,
} from '../../util/data';
import { ResponsiveImage, IconBannedUser, NamedLink, IconCollection } from '../../components/';
import { generateCitySlug, generateStoreSlug } from '../../util/seo';

import css from './Avatar.module.css';

// Responsive image sizes hint
const AVATAR_SIZES = '40px';
const AVATAR_SIZES_MEDIUM = '60px';
const AVATAR_SIZES_LARGE = '96px';

const AVATAR_IMAGE_VARIANTS = [
  // 40x40
  'square-xsmall',

  // 80x80
  'square-xsmall2x',

  // 240x240
  'square-small',

  // 480x480
  'square-small2x',
];

const VerifiedBadge = ({ verified, showVerifiedBadge }) => {
  if (!verified || !showVerifiedBadge) return null;
  return (
    <span className={css.verifiedBadge}>
      <IconCollection name="verifiedIcon" />
    </span>
  );
};

export const AvatarComponent = props => {
  const {
    rootClassName,
    className,
    initialsClassName,
    user,
    renderSizes,
    disableProfileLink,
    intl,
    showChangeProfileIcon,
    verified,
    showVerifiedBadge
  } = props;
  const classes = classNames(rootClassName || css.root, className);

  const userIsCurrentUser = user && user.type === 'currentUser';
  const avatarUser = userIsCurrentUser ? ensureCurrentUser(user) : ensureUser(user);
  const { profile = {} } = (avatarUser?.attributes) || {};
  const { publicData = {} } = (profile) || {};
  const { city, storeAddress } = (publicData) || {};
  const citySlug = generateCitySlug(storeAddress?.city || city);
  const isBannedUser = (avatarUser?.attributes?.banned) || false;
  const isDeletedUser = (avatarUser?.attributes?.deleted) || false;

  const defaultUserDisplayName = isBannedUser
    ? intl.formatMessage({ id: 'Avatar.bannedUserDisplayName' })
    : isDeletedUser
      ? intl.formatMessage({ id: 'Avatar.deletedUserDisplayName' })
      : '';

  const defaultUserAbbreviatedName = '';

  const displayName = userDisplayNameAsString(avatarUser, defaultUserDisplayName).trim();
  const abbreviatedName = userAbbreviatedName(avatarUser, defaultUserAbbreviatedName);
  const rootProps = { className: classes, title: displayName };
  const cloudinaryImageId = avatarUser?.attributes?.profile?.publicData?.profileImage?.public_id || null;

  const searchString = !!city && !!displayName
    ? `?name=${displayName}&city=${city}`
    : !!displayName
      ? `?name=${displayName}`
      : !!city
        ? `?city=${city}`
        : ''

  const linkProps = avatarUser?.id
    ? {
      name: 'ProfilePage',
      params: {
        ...(citySlug ? { city: citySlug } : {}),
        slug: generateStoreSlug(publicData?.storeName ?? displayName),
        id: avatarUser?.id?.uuid
      },
      to: { search: '' }
    }
    : { name: 'ProfileBasePage' };
  const hasProfileImage = avatarUser?.profileImage && avatarUser?.profileImage?.id;
  const profileLinkEnabled = !disableProfileLink && !['Sharetribe', 'Stripe'].includes(displayName);

  const AVATAR_PIXEL_SIZE = {
    [css.smallAvatar]:  40,
    [css.mediumAvatar]: 60,
    [css.largeAvatar]:  96,
  };
  const avatarPixelSize = AVATAR_PIXEL_SIZE[rootClassName] || 40;


  if (isBannedUser || isDeletedUser) {
    return (
      <div className={css.avatarContainer}>
        <div {...rootProps}>
          <IconBannedUser className={css.bannedUserIcon} />
        </div>
      </div>
    );
  } else if (hasProfileImage && profileLinkEnabled) {
    return (
      <div className={css.avatarContainer}>
        <NamedLink {...rootProps} {...linkProps}>
          <ResponsiveImage
            rootClassName={css.avatarImage}
            alt={displayName
              ? `${displayName} – rental owner on Rent Anything Store`
              : 'Rental owner on Rent Anything Store'}
            image={avatarUser?.profileImage}
            variants={AVATAR_IMAGE_VARIANTS}
            sizes={renderSizes}
            renderCloudinaryImage={cloudinaryImageId ? { public_id: cloudinaryImageId, borderRadius: '50%', height: 'inherit', width: 'inherit' } : null}
            isCritical={false}
            loading="eager"
            fetchPriority="high"
            decoding="async"
            width={avatarPixelSize}
            height={avatarPixelSize}
          />
        </NamedLink>
        <VerifiedBadge verified={verified} showVerifiedBadge={showVerifiedBadge} />
      </div>
    );
  } else if (hasProfileImage) {
    return (
      <div className={css.avatarContainer}>
        <div {...rootProps}>
          <ResponsiveImage
            rootClassName={css.avatarImage}
            alt={displayName
              ? `${displayName} – rental owner on Rent Anything Store`
              : 'Rental owner on Rent Anything Store'}
            image={avatarUser?.profileImage}
            variants={AVATAR_IMAGE_VARIANTS}
            sizes={renderSizes}
            isCritical={false}
            loading="eager"
            fetchPriority="high"
            decoding="async"
            width={avatarPixelSize}
            height={avatarPixelSize}
          />
        </div>
        <VerifiedBadge verified={verified} showVerifiedBadge={showVerifiedBadge} />
      </div>
    );
  } else if (profileLinkEnabled) {
    // Placeholder avatar (initials)
    return (
      <div className={css.avatarContainer}>
        <div {...rootProps}>
          <NamedLink {...linkProps}>
            <span className={classNames(initialsClassName || css.initials, css.name)}>{abbreviatedName}</span>
          </NamedLink>
          {showChangeProfileIcon && !disableProfileLink ? (
            <NamedLink name="ProfileSettingsPage" className={css.changeAvatarLarge} />
          ) : null}
        </div>
        <VerifiedBadge verified={verified} showVerifiedBadge={showVerifiedBadge} />
      </div>
    );
  } else {
    // Placeholder avatar (initials)
    return (
      <div className={css.avatarContainer}>
        <div {...rootProps}>
          <span className={classNames(initialsClassName || css.initials)}>{abbreviatedName}</span>
          {showChangeProfileIcon && !disableProfileLink ? (
            <NamedLink name="ProfileSettingsPage" className={css.changeAvatar} />
          ) : null}
        </div>
        <VerifiedBadge verified={verified} showVerifiedBadge={showVerifiedBadge} />
      </div>
    );
  }
};

AvatarComponent.defaultProps = {
  className: null,
  rootClassName: null,
  user: null,
  renderSizes: AVATAR_SIZES,
  disableProfileLink: false,
  verified: false,
  showVerifiedBadge: false,
};

AvatarComponent.propTypes = {
  rootClassName: string,
  className: string,
  user: oneOfType([propTypes.user, propTypes.currentUser]),

  renderSizes: string,
  disableProfileLink: bool,
  verified: bool,
  showVerifiedBadge: bool,
  // from injectIntl
  intl: intlShape.isRequired,
};

const Avatar = injectIntl(AvatarComponent);

export default Avatar;

export const AvatarSmall = props => (
  <Avatar
    rootClassName={css.smallAvatar}
    initialsClassName={css.initialsSmall}
    renderSizes={AVATAR_SIZES_MEDIUM}
    {...props}
  />
);
AvatarSmall.displayName = 'AvatarSmall';

export const AvatarMedium = props => (
  <Avatar
    rootClassName={css.mediumAvatar}
    initialsClassName={css.initialsMedium}
    renderSizes={AVATAR_SIZES_MEDIUM}
    {...props}
  />
);
AvatarMedium.displayName = 'AvatarMedium';

export const AvatarLarge = props => (
  <Avatar
    rootClassName={css.largeAvatar}
    initialsClassName={css.initialsLarge}
    renderSizes={AVATAR_SIZES_LARGE}
    {...props}
  />
);
AvatarLarge.displayName = 'AvatarLarge';
